import React, { useState, useEffect } from 'react';
import { Input, Button, List, message, Tag, Spin } from 'antd';
import { supabase } from './supabaseClient';
import { useTranslation } from 'react-i18next';
import './AIChatInterface.css';  // CSS 파일 import

const AIChatInterface = ({ meetingId, transcript, onClose }) => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        fetchExistingMessages();
    }, [meetingId, transcript]);

    const fetchExistingMessages = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('ai_interactions')
                .select('*')
                .eq('meeting_id', meetingId)
                .order('created_at', { ascending: true });

            if (error) throw error;

            const formattedMessages = data.map(item => [
                { type: 'question', content: item.question },
                item.answer
                    ? { type: 'answer', content: item.answer }
                    : { type: 'loading', content: t('AI is generating an answer...') }
            ]).flat();

            setMessages(formattedMessages);
        } catch (error) {
            console.error('Error fetching messages:', error);
            message.error(t('Failed to load messages'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendMessage = async () => {
        if (inputValue.trim() === '') return;

        const newQuestion = { type: 'question', content: inputValue };
        const loadingMessage = { type: 'loading', content: t('AI is generating an answer...') };
        setMessages(prevMessages => [...prevMessages, newQuestion, loadingMessage]);
        setInputValue('');

        try {
            const { data, error } = await supabase
                .from('ai_interactions')
                .insert({
                    meeting_id: meetingId,
                    transcription_id: transcript ? transcript.uuid : null,
                    question: inputValue,
                })
                .single();

            if (error) throw error;

            // 여기서 AI 응답을 기다리는 로직을 구현해야 합니다.
            // 예를 들어, 주기적으로 데이터베이스를 확인하거나 웹소켓을 사용할 수 있습니다.
            // 지금은 임시로 5초 후에 더미 응답을 생성합니다.
            setTimeout(async () => {
                const dummyAnswer = "이것은 AI의 더미 응답입니다.";
                const { data: updateData, error: updateError } = await supabase
                    .from('ai_interactions')
                    .update({ answer: dummyAnswer })
                    .eq('id', data.id);

                if (updateError) throw updateError;

                setMessages(prevMessages =>
                    prevMessages.map(msg =>
                        msg.type === 'loading' ? { type: 'answer', content: dummyAnswer } : msg
                    )
                );
            }, 5000);

        } catch (error) {
            console.error('Error sending message:', error);
            message.error(t('Failed to send message'));
            setMessages(prevMessages => prevMessages.filter(msg => msg.type !== 'loading'));
        }
    };

    const renderMessage = (item) => {
        switch (item.type) {
            case 'question':
                return (
                    <div className="message-item question-container">
                        <Tag color="blue">{t('Question')}</Tag>
                        <div className="message-content">{item.content}</div>
                    </div>
                );
            case 'answer':
                return (
                    <div className="message-item answer-container">
                        <Tag color="green">{t('Answer')}</Tag>
                        <div className="message-content">
                            {item.content.split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                    {line}
                                    <br />
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                );
            case 'loading':
                return (
                    <div className="message-item loading-container">
                        <Spin /> {item.content}
                    </div>
                );
            default:
                return null;
        }
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div className="ai-chat-interface">
            {transcript && (
                <Tag color="purple" className="context-tag">
                    {t("Context")}: {formatTime(transcript.start_time)} - {formatTime(transcript.end_time)}
                </Tag>
            )}
            <List
                className="message-list"
                loading={isLoading}
                dataSource={messages}
                renderItem={renderMessage}
            />
            <div className="input-area">
                <Input
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onPressEnter={e => {
                        e.preventDefault();
                        handleSendMessage();
                    }}
                    placeholder={t("Type your question here")}
                />
                <Button onClick={handleSendMessage} type="primary">{t("Send")}</Button>
            </div>
        </div>
    );
};

export default AIChatInterface;